/**
 * The design studio (docs/17-asset-library/14-design-studio.md §9, §11.1):
 * the organisation's designs, one design's detail, and the writes on it. A
 * design's socket (`modeling/useModelSocket.ts`) keeps the detail fresh while
 * a step runs; every write asks for the detail, the list and the balance
 * again.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "./client";
import { ME_KEY } from "./me";
import type {
  ForAd,
  ModelDetail,
  ModelDressing,
  ModelEngine,
  ModelList,
  ModelSelection,
  ModelValues,
} from "./types";

export const MODELS_KEY = ["models"] as const;
export const MODEL_KEY = (id: string) => ["models", id] as const;

const path = (id: string) => `/v1/models/${encodeURIComponent(id)}`;

/** `GET /v1/models`, the design last changed first, paged by cursor. */
export function useModels() {
  return useInfiniteQuery({
    queryKey: MODELS_KEY,
    queryFn: ({ pageParam }) =>
      api<ModelList>(
        `/v1/models${pageParam ? `?cursor=${encodeURIComponent(pageParam)}` : ""}`,
      ),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
}

/** How often a busy design is asked for while its socket is not open. */
export const MODEL_POLL_MS = 5_000;
/** How often a design being added to Objects is asked for, socket or not. */
export const PUBLISH_POLL_MS = 3_000;

/** `GET /v1/models/{id}`. The addresses in a detail last fifteen minutes.
 *  With `pollBusy` (the socket is not open) a design that is not idle is
 *  asked for again every 5 s. A design that is `publishing` is asked for
 *  every 3 s whatever the socket says, so a socket that dropped the
 *  `published` event never leaves "Adding…" on the page. */
export function useModel(
  id: string,
  { pollBusy = false }: { pollBusy?: boolean } = {},
) {
  return useQuery({
    queryKey: MODEL_KEY(id),
    queryFn: () => api<ModelDetail>(path(id)),
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      if (state === "publishing") return PUBLISH_POLL_MS;
      return pollBusy && state !== undefined && state !== "idle"
        ? MODEL_POLL_MS
        : false;
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** After a write: the design, the list of designs, and the balance. */
function useAfterWrite(id?: string) {
  const queryClient = useQueryClient();
  return (other?: string) => {
    const which = id ?? other;
    if (which) {
      void queryClient.invalidateQueries({ queryKey: MODEL_KEY(which) });
    }
    // The list alone: the prefix would also ask for every open design again.
    void queryClient.invalidateQueries({ queryKey: MODELS_KEY, exact: true });
    void queryClient.invalidateQueries({ queryKey: ME_KEY });
  };
}

export interface NewModel {
  prompt: string;
  name?: string;
  refs?: string[];
  real_size_m?: number;
  /** `"mesh"`: a sculpted design ([sculpted §2.2]); absent: a built one. */
  engine?: ModelEngine;
  /** Made for an ad's cast row (docs/SPEC.md §6.4). */
  for_ad?: ForAd;
}

/** `POST /v1/models`: a design and its first instruction. */
export function useCreateModel() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (body: NewModel) =>
      api<{ id: string; revision: number }>("/v1/models", {
        method: "POST",
        body,
      }),
    onSuccess: (reply) => after(reply.id),
  });
}

/** `POST …/turns`: one instruction, with what the user pointed at. */
export function useSendTurn(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (body: { instruction: string; selection?: ModelSelection }) =>
      api<{ revision: number }>(`${path(id)}/turns`, { method: "POST", body }),
    onSuccess: () => after(),
  });
}

/** `POST …/turns` on a sculpted design ([sculpted §2.2]): a generation.
 *  Without a description it is **Try again** (the same description, the next
 *  seed); with one, a new description. */
export function useGenerate(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (instruction?: string) =>
      api<{ revision: number }>(`${path(id)}/turns`, {
        method: "POST",
        body: instruction === undefined ? {} : { instruction },
      }),
    onSuccess: () => after(),
  });
}

/** `POST …/turns/{n}/cancel`: **Stop**. Whatever it answers, the design is
 *  asked for again: a stop that arrives after the end is not an error. */
export function useCancelTurn(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (n: number) =>
      api<{ revision: number }>(`${path(id)}/turns/${n}/cancel`, {
        method: "POST",
      }),
    onSettled: () => after(),
  });
}

/** `POST …/params`: new values of the design's own settings; a free build.
 *  The reply's `revision` is the current step when nothing changed. */
export function useSetParams(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (values: ModelValues) =>
      api<{ revision: number }>(`${path(id)}/params`, {
        method: "POST",
        body: { values },
      }),
    onSuccess: () => after(),
  });
}

/** `PATCH …/dressing`: what is on the current step's surfaces. */
export function usePatchDressing(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (dressing: ModelDressing) =>
      api<ModelDressing>(`${path(id)}/dressing`, {
        method: "PATCH",
        body: dressing,
      }),
    onSuccess: () => after(),
  });
}

/** `POST …/revisions/{n}/restore`. `rebuilding` is true when the step's
 *  files had been cleaned up and a free rebuild was queued (202); false when
 *  the step was copied and is current at once (201). */
export function useRestore(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: async (n: number) => {
      let status = 201;
      const reply = await api<{ revision: number }>(
        `${path(id)}/revisions/${n}/restore`,
        { method: "POST", onStatus: (s) => (status = s) },
      );
      return { revision: reply.revision, rebuilding: status === 202 };
    },
    onSuccess: () => after(),
  });
}

export interface ModelChanges {
  name?: string;
  refs?: string[];
  real_size_m?: number | null;
}

/** `PATCH /v1/models/{id} {for_ad}` for a design chosen from the cast
 *  panel's picker (docs/SPEC.md §6.4): the design is opened for that row. */
export function useCarryThread() {
  const queryClient = useQueryClient();
  const after = useAfterWrite();
  return useMutation({
    mutationFn: ({ id, for_ad }: { id: string; for_ad: ForAd }) =>
      api<ModelDetail>(path(id), { method: "PATCH", body: { for_ad } }),
    onSuccess: (detail, { id }) => {
      queryClient.setQueryData(MODEL_KEY(id), detail);
      after(id);
    },
  });
}

/** What **Use in {ad}** answers (docs/SPEC.md §6.4). `publishing`: the
 *  design is being added to Objects and the change follows it. */
export interface UsedInAd {
  ad_id: string;
  change: { id: string; n: number; state: string } | null;
  sentence: string | null;
  publishing: boolean;
}

/** What the studio hands the ad's page after **Use in {ad}** (its router
 *  state): the page says what happens to the film. */
export interface FromStudio {
  usedInAd?: UsedInAd;
}

/** `POST /v1/models/{id}/use-in-ad`. */
export function useUseInAd(id: string) {
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: () =>
      api<UsedInAd>(`${path(id)}/use-in-ad`, { method: "POST" }),
    onSuccess: () => after(),
  });
}

/** `PATCH /v1/models/{id}`: the name, the reference photos, the real size.
 *  The reply is the detail, which replaces the one held. */
export function usePatchModel(id: string) {
  const queryClient = useQueryClient();
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (changes: ModelChanges) =>
      api<ModelDetail>(path(id), { method: "PATCH", body: changes }),
    onSuccess: (detail) => {
      queryClient.setQueryData(MODEL_KEY(id), detail);
      after();
    },
  });
}

/** `POST …/publish`: add the design to the 3D library, or update it there.
 *  The body is always a JSON object. */
export function usePublish(id: string) {
  const queryClient = useQueryClient();
  const after = useAfterWrite(id);
  return useMutation({
    mutationFn: (body: { name?: string } = {}) =>
      api<{ state: "publishing" }>(`${path(id)}/publish`, {
        method: "POST",
        body,
      }),
    onSuccess: (reply) => {
      // The design is busy from this reply on, and its last failure is gone:
      // the page says "Adding…" without waiting for the detail to come again.
      queryClient.setQueryData<ModelDetail>(MODEL_KEY(id), (held) =>
        held ? { ...held, state: reply.state, publish_error_code: null } : held,
      );
      after();
    },
  });
}

/** `POST /v1/models {from_asset}`: a design started from an object of the 3D
 *  library that carries its generator ("Make my own version"), or the design
 *  that published it ("Open in the design studio": no copy is made). */
export function useReshape() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (shapeId: string) =>
      api<{ id: string; revision: number }>("/v1/models", {
        method: "POST",
        body: { from_asset: { kind: "shape", id: shapeId } },
      }),
    onSuccess: (reply) => after(reply.id),
  });
}

/** `DELETE /v1/models/{id}`. */
export function useDeleteModel() {
  const after = useAfterWrite();
  return useMutation({
    mutationFn: (id: string) => api<void>(path(id), { method: "DELETE" }),
    onSuccess: () => after(),
  });
}
